import watchApplicationReload from '@Application/ApplicationSettings/ApplicationUpdate/watchApplicationReload';
import watchApplicationUpdate from '@Application/ApplicationSettings/ApplicationUpdate/watchApplicationUpdate';
import * as React from 'react';

export type UseApplicationUpdateOptions = {
	readonly watchUpdate?: typeof watchApplicationUpdate;
	readonly watchReload?: typeof watchApplicationReload;
	readonly reloadPage?: () => void;
};

export type ApplicationUpdate = {
	readonly isUpdateAvailable: boolean;
	readonly applyUpdate: () => void;
};

/**
 * `watchUpdate` and `watchReload` default to the real service worker plumbing but are injectable:
 * `navigator.serviceWorker` is `undefined` under jsdom, so without this seam the branches below
 * would never be exercised by a test. Neither option ever exposes a service worker type — this
 * hook only ever learns "an update is waiting" and "how to apply it".
 */
function useApplicationUpdate(options: UseApplicationUpdateOptions = {}): ApplicationUpdate {
	const watchUpdate = options.watchUpdate ?? watchApplicationUpdate;
	const watchReload = options.watchReload ?? watchApplicationReload;

	const [applyPendingUpdate, setApplyPendingUpdate] = React.useState<(() => void) | null>(null);

	React.useEffect(() => {
		return watchUpdate({
			onUpdateAvailable: (applyAvailableUpdate) => setApplyPendingUpdate(() => applyAvailableUpdate),
		});
	}, [watchUpdate]);

	React.useEffect(() => {
		// Built inside the effect so the default closure is not recreated on every render: only
		// `options.reloadPage` — stable across renders when the caller omits it — is a dependency.
		const reloadPage = options.reloadPage ?? (() => window.location.reload());

		return watchReload({ onControllerChange: reloadPage });
	}, [watchReload, options.reloadPage]);

	// The hook never decides to reload on its own: it only relays whatever `watchReload` reports,
	// which itself only reacts to the platform's own controllerchange event.
	const applyUpdate = React.useCallback((): void => {
		applyPendingUpdate?.();
	}, [applyPendingUpdate]);

	return {
		isUpdateAvailable: applyPendingUpdate !== null,
		applyUpdate,
	};
}

export default useApplicationUpdate;
